import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { Arc, Atmosphere, CountryRegion, GeoLines, Globe, LandFill, makeCloudTexture, makeTriangleTexture, Pops, Pulse, SurfaceGlyphs, type CountryRing, type LatLon, type PopMarker } from '@tiw/globe';
import { tokens } from '@tiw/ui';

const C = tokens.color.globe;
import { useViewportStore } from '@/stores/useViewportStore';
import { useTelemetryStore } from '@/stores/useTelemetryStore';
import { useTopologySnapshot } from '@/hooks/useTopologySnapshot';
import { useGeoLines } from '@/hooks/useGeoLines';
import { usePoints } from '@/hooks/usePoints';
import { useLandMask } from '@/hooks/useLandMask';
import { useCountryShapes } from '@/hooks/useCountryShapes';
import { useFlowStore } from '@/stores/useFlowStore';
import { useIncidentStore } from '@/stores/useIncidentStore';
import { useSelectionStore } from '@/stores/useSelectionStore';
import { CatmullRomCurve3, type Group, Vector3 } from 'three';

/** Cinematic intro: camera flies along this spline (space -> overhead) once. */
const INTRO_DURATION_S = 3.2;
const easeOutCubic = (t: number): number => 1 - (1 - t) ** 3;

// Two PoPs closer than this (equirectangular degrees, ~33 km) are treated as
// co-located: the link between them is dropped so it doesn't draw as a spike.
const MIN_ARC_DEG = 0.3;
const tooClose = (a: LatLon, b: LatLon): boolean => {
  const dLat = a.lat - b.lat;
  const dLon = (a.lon - b.lon) * Math.cos((a.lat * Math.PI) / 180);
  return Math.hypot(dLat, dLon) < MIN_ARC_DEG;
};

/**
 * Globe scene.
 *
 * Composes the Globe package with lighting and a controlled camera, and
 * renders the D1-backed topology snapshot: PoPs as instanced markers on the
 * surface, links as great-circle arcs. Live flow animation is P2 work. See
 * docs/FRONTEND_PLAN.md and docs/API_CONTRACTS.md.
 */
export function GlobeScene(): JSX.Element {
  const groupRef = useRef<Group>(null);
  const quality = useViewportStore((s) => s.qualityTier);
  const isPlaying = useViewportStore((s) => s.isPlaying);
  const cameraTarget = useViewportStore((s) => s.cameraTarget);
  const introComplete = useViewportStore((s) => s.introComplete);
  const completeIntro = useViewportStore((s) => s.completeIntro);
  const setTelemetry = useTelemetryStore((s) => s.setSample);

  const hoveredId = useSelectionStore((s) => s.hoveredId);
  const selectedId = useSelectionStore((s) => s.selected?.id ?? null);
  const setHovered = useSelectionStore((s) => s.setHovered);
  const select = useSelectionStore((s) => s.select);
  const clearSelection = useSelectionStore((s) => s.clear);

  const introT = useRef(0);
  const introCurve = useMemo(
    () =>
      new CatmullRomCurve3([
        new Vector3(0, 5.2, 18), // distant, high above the pole
        new Vector3(0, 3.0, 11.5),
        new Vector3(0, 1.1, 7.6),
        new Vector3(0, 0, 6), // resting overhead view (matches <Canvas> camera)
      ]),
    [],
  );

  const { snapshot } = useTopologySnapshot();
  const coastlines = useGeoLines('/data/coastlines.geojson');
  const cables = useGeoLines('/data/cables.geojson');
  const cloudRegions = usePoints('/data/clouds.geojson');
  const ixps = usePoints('/data/ixps.geojson');
  const facilities = usePoints('/data/facilities.geojson');
  const landMask = useLandMask('/data/land.geojson');
  const countryShapes = useCountryShapes('/data/countries.geojson');
  const flowByLink = useFlowStore((s) => s.byLink);
  const incidents = useIncidentStore((s) => s.active);
  const popMarkers = useMemo<readonly PopMarker[]>(
    () =>
      snapshot?.pops.map((p) => ({
        id: p.id,
        lat: p.location.lat,
        lon: p.location.lon,
        online: p.online,
      })) ?? [],
    [snapshot],
  );

  const popById = useMemo<ReadonlyMap<string, LatLon>>(
    () =>
      new Map((snapshot?.pops ?? []).map((p) => [p.id, { lat: p.location.lat, lon: p.location.lon }])),
    [snapshot],
  );

  // Links → great-circle arcs between their endpoint PoPs. Per-flow <Arc>
  // children are fine at this scale (~14); converting to InstancedMesh once the
  // count passes ~50 is P3 work — see docs/BUILD_PLAN.md. `capBps` lets the
  // live flow tick (bps) be normalized to a 0..1 intensity per arc.
  const arcs = useMemo<ReadonlyArray<{ id: string; from: LatLon; to: LatLon; capBps: number }>>(() => {
    if (!snapshot) return [];
    // Collapse arcs that connect the same two *locations*. Many IXPs share one
    // physical metro (often identical coords — e.g. several PeeringDB IXPs in
    // Tokyo), so id-distinct links between them would otherwise stack as
    // overlapping arcs and read as bidirectional. Treat endpoints within
    // MIN_ARC_DEG as the same node, and ignore arc direction.
    const kept: { id: string; from: LatLon; to: LatLon; capBps: number }[] = [];
    for (const link of snapshot.links) {
      const from = popById.get(link.src);
      const to = popById.get(link.dst);
      // Skip degenerate links between near-coincident PoPs (~0-length arc that
      // renders as a vertical self-loop spike).
      if (!from || !to || tooClose(from, to)) continue;
      const duplicate = kept.some(
        (a) =>
          (tooClose(from, a.from) && tooClose(to, a.to)) ||
          (tooClose(from, a.to) && tooClose(to, a.from)),
      );
      if (duplicate) continue;
      kept.push({ id: link.id, from, to, capBps: link.capacity_gbps * 1_000_000_000 });
    }
    return kept;
  }, [snapshot, popById]);

  // Real incidents (IODA outages, provider status) that are geolocatable →
  // a pulse at each site.
  const incidentPulses = useMemo(
    () => Object.values(incidents).filter((i) => i.lat != null && i.lon != null),
    [incidents],
  );

  // Country-level outages (IODA) whose country we have a boundary for → light up
  // the whole shape. The ISO-2 code rides in the incident id (`ioda-DM`,
  // `ioda-DM-1781110200`). Tiny island nations absent from the 110m set fall
  // back to just the point pulse above. See pipeline/countries.py (P5).
  const incidentCountries = useMemo(() => {
    const out: Array<{ id: string; rings: readonly CountryRing[] }> = [];
    for (const inc of Object.values(incidents)) {
      if (inc.source !== 'ioda') continue;
      const code = /^ioda-([A-Za-z]{2})(?:-|$)/.exec(inc.id)?.[1]?.toUpperCase();
      const rings = code ? countryShapes.get(code) : undefined;
      if (rings) out.push({ id: inc.id, rings });
    }
    return out;
  }, [incidents, countryShapes]);

  const { camera, gl } = useThree();

  useEffect(() => {
    // Reset perf counters on quality change.
    gl.info.reset();
  }, [quality, gl]);

  // Snap to the start of the intro spline before the first frame paints, so
  // there's no flash at the resting position. Skips if the intro already ran.
  useLayoutEffect(() => {
    if (introComplete) return;
    introT.current = 0;
    camera.position.copy(introCurve.getPoint(0));
    camera.lookAt(0, 0, 0);
  }, [introComplete, camera, introCurve]);

  useFrame((state, delta) => {
    if (!introComplete) {
      introT.current = Math.min(1, introT.current + delta / INTRO_DURATION_S);
      state.camera.position.copy(introCurve.getPoint(easeOutCubic(introT.current)));
      state.camera.lookAt(0, 0, 0);
      if (introT.current >= 1) completeIntro();
    }
    if (groupRef.current && isPlaying) {
      groupRef.current.rotation.y += delta * 0.04;
    }
    setTelemetry({
      fps: delta > 0 ? Math.round(1 / delta) : 0,
      frameTimeMs: delta * 1000,
      drawCalls: gl.info.render.calls,
    });
  });

  return (
    <group ref={groupRef}>
      <ambientLight intensity={0.18} />
      <directionalLight position={[5, 3, 5]} intensity={1.1} color={C.keyLight} />
      <directionalLight position={[-4, -2, -3]} intensity={0.35} color={C.fillLight} />

      {/* Layer stack by shape size: biggest shape draws first (bottom), smallest
          last (top), so a smaller marker is never hidden behind a bigger one.
          renderOrder ladder: strips(-10) < coastlines(-8) < cables(-7) <
          PoPs(-6) < clouds(-5) < IXP triangles(-4) < facility dots(-3) < flows(0). */}
      <Globe baseColor={C.surface} rimColor={C.rim}>
        <LandFill mask={landMask} />
        <GeoLines lines={coastlines} color={C.coastline} opacity={0.45} radius={1.0015} renderOrder={-8} />
        <GeoLines lines={cables} color={C.cable} opacity={0.45} radius={1.002} renderOrder={-7} />
        <SurfaceGlyphs
          points={cloudRegions}
          color={C.cloudRegion}
          size={0.05}
          opacity={0.95}
          lift={1.013}
          makeTexture={makeCloudTexture}
          renderOrder={-5}
        />
        {/* IXPs: interconnection hubs — neon-green upward triangle, additive so it
            glows like the PoPs. */}
        <SurfaceGlyphs
          points={ixps}
          color={C.ixp}
          size={0.038}
          opacity={0.95}
          lift={1.014}
          makeTexture={makeTriangleTexture}
          additive
          renderOrder={-4}
        />
        {/* Facilities: the ambient substrate of data centers — tiny pink square dots
            (smallest shape → drawn on top so they're never buried). */}
        <SurfaceGlyphs points={facilities} color={C.facility} size={0.007} opacity={0.6} lift={1.004} renderOrder={-3} />
        {popMarkers.length > 0 ? (
          <Pops
            pops={popMarkers}
            color={C.pop}
            size={0.018}
            selectedId={selectedId}
            hoveredId={hoveredId}
            renderOrder={-6}
            onHover={(id) => {
              if (id !== hoveredId) setHovered(id);
            }}
            onSelect={(id) => {
              const pop = id && snapshot ? snapshot.pops.find((p) => p.id === id) : undefined;
              if (!pop || selectedId === id) {
                clearSelection();
                return;
              }
              select({
                id: pop.id,
                name: pop.name,
                regionCode: pop.region_code,
                capacityGbps: pop.capacity_gbps,
                online: pop.online,
              });
            }}
            onClear={clearSelection}
          />
        ) : null}
        {arcs.map((arc) => {
          const sample = flowByLink[arc.id];
          const intensity = sample ? Math.min(1, sample.bps_forward / arc.capBps) : 0.4;
          return (
            <Arc key={arc.id} from={arc.from} to={arc.to} color={C.link} intensity={intensity} />
          );
        })}

        {/* Country-level outages (IODA): the whole country's border breathes. */}
        {incidentCountries.map((c) => (
          <CountryRegion key={c.id} rings={c.rings} color={C.incident} />
        ))}

        {/* Real incidents (IODA outages / provider status): a pulse on-site. */}
        {incidentPulses.map((inc) => (
          <Pulse key={inc.id} lat={inc.lat as number} lon={inc.lon as number} color={C.incident} />
        ))}
      </Globe>
      <Atmosphere scale={1.03} intensity={1.1} color={C.rim} />

      <OrbitControls
        makeDefault
        enabled={introComplete}
        enablePan={false}
        enableDamping
        dampingFactor={0.08}
        minDistance={2.2}
        maxDistance={12}
        target={cameraTarget}
      />
    </group>
  );
}
